import { useForm } from "react-hook-form";
import { useEffect, useState, useCallback } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import BackButton from "../common/BackButton";
import BookManagerService from "../../services/dashboard/BookManager";
import CategoryManager from "../../services/dashboard/CategoryManager";
import AuthorManager from "../../services/dashboard/AuthorManager";
import BookMetadataService from "../../services/dashboard/BookMetadataService";
import BookBarcodeScannerModal from "./BookBarcodeScannerModal";
import config from "../../config";
import { handleApiResponse } from "../../utils/apiResponseHandler";
import Select from "react-select";
import debounce from "lodash.debounce";
import AsyncSelect from "react-select/async";
import { Controller } from "react-hook-form";
import { Camera, Search, Sparkles, CheckCircle2, AlertTriangle } from "lucide-react";

const BookAddForm = ({ setEditingBook, setShowModal }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    control,
  } = useForm();

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      padding: "0.25rem 0.5rem",
      borderRadius: "0.375rem",
      borderColor: state.isFocused
        ? "#FF864A" // primary-shade4 on focus
        : "#E0E0E0", // primary-shade3 by default
      boxShadow: state.isFocused ? "0 0 0 2px rgba(255, 134, 74, 0.5)" : "none", // focus:ring effect
      "&:hover": {
        borderColor: "#FF864A", // on hover
      },
      backgroundColor: "#fff",
      minHeight: "2.5rem",
      fontSize: "0.875rem",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#FF864A"
        : state.isFocused
        ? "#FFE4D9"
        : "#fff",
      color: state.isSelected ? "#fff" : "#000",
      fontSize: "0.875rem",
      cursor: "pointer",
    }),
    menu: (base) => ({
      ...base,
      zIndex: 20,
    }),
  };

  const [bookImages, setBookImages] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();
  const { bookId } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [duplicateBooks, setDuplicateBooks] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [bookNameInput, setBookNameInput] = useState("");

  // ─── Book Scanner & AI Auto-fill States ─────────────────────────────────────
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isFetchingMetadata, setIsFetchingMetadata] = useState(false);
  const [missingAuthorInfo, setMissingAuthorInfo] = useState(null);
  const [isAddingAuthor, setIsAddingAuthor] = useState(false);

  const authorOptions = authors.map((author) => ({
    value: author.authorId,
    label: author.authorName,
  }));

  const categoryOptions = categories.map((category) => ({
    value: category.catId,
    label: category.catName,
  }));

  const loadOptions = async (inputValue, callback) => {
    if (!inputValue || inputValue.length < 3) {
      callback([]);
      return;
    }

    try {
      const result = await BookManagerService.searchBooks(inputValue);
      const books = result.responseData || [];
      const options = books.map((book) => ({
        value: book.bookId,
        label: `${book.bookName} (${book.bookEdition || "N/A"})`,
      }));
      callback(options);
    } catch (err) {
      console.error("Book search failed", err);
      callback([]);
    }
  };

  // Fetch authors and categories
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [authorRes, categoryRes] = await Promise.all([
          AuthorManager.getAllAuthors(),
          CategoryManager.getAllCategories(),
        ]);

        setAuthors(handleApiResponse(authorRes) || []);
        setCategories(handleApiResponse(categoryRes) || []);
      } catch (error) {
        console.error("Failed to load dropdown data:", error);
        toast.error("Failed to load authors or categories.");
      }
    };

    fetchDropdownData();
  }, []);

  // Load book if editing
  useEffect(() => {
    const fetchBook = async () => {
      if (!bookId || authors.length === 0 || categories.length === 0) return;

      try {
        const res = await BookManagerService.getBookById(bookId);
        const book = handleApiResponse(res);
        if (!book) return;

        const defaults = {
          bookId: book.bookId,
          bookName: book.bookName,
          bookPublication: book.bookPublication,
          bookIsbn10: book.bookIsbn10,
          bookIsbn13: book.bookIsbn13,
          publishedDate: book.publishedDate,
          pages: book.pages,
          height: book.height,
          width: book.width,
          length: book.length,
          weight: book.weight,
          readingAge: book.readingAge,
          bookDescription: book.bookDescription,
          bookCopies: book.bookCopies,
          bookGenres: book.bookGenres,
          bookEdition: book.bookEdition,
          bookLanguage: book.bookLanguage,
          bookStatus: book.bookStatus,
          bookStockStatus: book.bookStockStatus,
          categoryId: book.categoryId,
          authorId: book.authorId,
        };

        reset(defaults);
        setBookImages(book.bookImages || []);
        setBookNameInput(`${book.bookName}`);
      } catch (err) {
        console.error("Failed to load book:", err);
        toast.error("Failed to load book details.");
      }
    };

    fetchBook();
  }, [bookId, authors.length, categories.length, reset]);

  const onSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      const filteredFormData = { ...formData };
      delete filteredFormData.author;
      delete filteredFormData.category;
      delete filteredFormData.isInWishlist;
      delete filteredFormData.isInCart;

      let res;

      if (bookId) {
        res = await BookManagerService.updateBook(filteredFormData, bookImages);
        if (handleApiResponse(res, "Book updated successfully!")) {
          navigate("/bookManager");
        }
      } else {
        res = await BookManagerService.addBook(filteredFormData, bookImages);
        if (handleApiResponse(res, "Book created successfully!")) {
          navigate("/bookManager");
        }
      }

      reset();
      setBookImages([]);
      if (setEditingBook) setEditingBook(null);
    } catch (error) {
      toast.error(error.message || "Error saving book.");
      console.error(error);
    } finally {
      setIsSubmitting(false); // Stop loader
    }
  };

  // ─── Match Author by name in existing authors list ────────────────────────
  const findMatchingAuthor = useCallback(
    (scannedName) => {
      if (!scannedName || !authors || authors.length === 0) return null;
      const clean = scannedName.trim().toLowerCase();

      // 1. Exact match
      let match = authors.find(
        (a) => a.authorName && a.authorName.trim().toLowerCase() === clean
      );
      if (match) return match;

      // 2. Contains match
      match = authors.find((a) => {
        if (!a.authorName) return false;
        const an = a.authorName.trim().toLowerCase();
        return an.includes(clean) || clean.includes(an);
      });
      if (match) return match;

      // 3. Token match
      const tokens = clean.split(/[\s,.]+/).filter((t) => t.length > 2);
      if (tokens.length > 0) {
        match = authors.find((a) => {
          if (!a.authorName) return false;
          const an = a.authorName.trim().toLowerCase();
          return tokens.every((token) => an.includes(token));
        });
      }
      return match || null;
    },
    [authors]
  );

  // ─── Apply Book Data to Form Fields ────────────────────────────────────────
  const applyBookDataToForm = useCallback(
    async (bookData, matchedAuthorId) => {
      setValue("bookName", bookData.title, { shouldValidate: true });
      setBookNameInput(bookData.title);

      if (bookData.publisher) {
        setValue("bookPublication", bookData.publisher, { shouldValidate: true });
      }
      if (bookData.isbn10) setValue("bookIsbn10", bookData.isbn10, { shouldValidate: true });
      if (bookData.isbn13) setValue("bookIsbn13", bookData.isbn13, { shouldValidate: true });
      if (bookData.pages) setValue("pages", bookData.pages, { shouldValidate: true });
      if (bookData.publishedDate) setValue("publishedDate", bookData.publishedDate, { shouldValidate: true });
      if (bookData.language) setValue("bookLanguage", bookData.language, { shouldValidate: true });
      if (bookData.genre) setValue("bookGenres", bookData.genre, { shouldValidate: true });
      if (bookData.readingAge) setValue("readingAge", bookData.readingAge, { shouldValidate: true });
      if (bookData.description) setValue("bookDescription", bookData.description, { shouldValidate: true });

      setValue("bookEdition", "1st Edition", { shouldValidate: true });
      setValue("bookCopies", 1, { shouldValidate: true });
      setValue("bookStatus", "ACTIVE", { shouldValidate: true });
      setValue("bookStockStatus", "IN_STOCK", { shouldValidate: true });

      if (matchedAuthorId) {
        setValue("authorId", matchedAuthorId, { shouldValidate: true });
      }

      // Auto-match Category if found
      if (categories && categories.length > 0) {
        const matchedCategory = categories.find((cat) => {
          const cn = (cat.catName || "").toLowerCase();
          const gn = (bookData.genre || "").toLowerCase();
          return (
            cn.includes(gn) ||
            gn.includes(cn) ||
            (bookData.categories &&
              bookData.categories.some((c) => (typeof c === "string" ? c.toLowerCase().includes(cn) : false)))
          );
        });
        if (matchedCategory) {
          setValue("categoryId", matchedCategory.catId, { shouldValidate: true });
        }
      }

      // Auto-attach cover image if available and no user images uploaded yet
      if (bookData.coverImageUrl && bookImages.length === 0) {
        try {
          const file = await BookMetadataService.urlToFile(
            bookData.coverImageUrl,
            `${bookData.title.slice(0, 15).replace(/[^a-z0-9]/gi, "_")}_cover.jpg`
          );
          if (file) {
            setBookImages([file]);
          }
        } catch (err) {
          console.warn("Could not download cover image file:", err);
        }
      }
    },
    [categories, bookImages.length, setValue]
  );

  // ─── Handle Scanned ISBN ───────────────────────────────────────────────────
  const handleIsbnScan = async (isbn) => {
    setIsFetchingMetadata(true);
    try {
      const bookData = await BookMetadataService.fetchByIsbn(isbn);
      setIsScannerOpen(false);

      // Check if author exists in system
      const matched = findMatchingAuthor(bookData.authorName);

      if (matched) {
        await applyBookDataToForm(bookData, matched.authorId);
        setMissingAuthorInfo(null);
        toast.success(
          `Book "${bookData.title}" auto-filled! Author: ${matched.authorName}`
        );
      } else {
        // Author NOT registered in database
        setMissingAuthorInfo({
          scannedAuthorName: bookData.authorName,
          bookData: bookData,
          authorProfile: null,
          isLoadingProfile: true,
        });

        toast.error(
          `Author "${bookData.authorName}" is not added yet! Please add this author first.`,
          { duration: 5000 }
        );

        // Pre-fetch author profile (photo & biography) from Wikipedia / OpenLibrary
        BookMetadataService.fetchAuthorProfile(bookData.authorName)
          .then((profile) => {
            setMissingAuthorInfo((prev) =>
              prev ? { ...prev, authorProfile: profile, isLoadingProfile: false } : null
            );
          })
          .catch(() => {
            setMissingAuthorInfo((prev) =>
              prev ? { ...prev, isLoadingProfile: false } : null
            );
          });
      }
    } catch (err) {
      console.warn("Book scan lookup notice:", err);
      setIsScannerOpen(false);

      // Even if online metadata wasn't found, ALWAYS populate the scanned ISBN in the form!
      if (isbn && isbn.length === 10) {
        setValue("bookIsbn10", isbn);
      } else if (isbn && isbn.length === 13) {
        setValue("bookIsbn13", isbn);
      }

      toast.success(
        `Scanned ISBN (${isbn}) filled in form! Online details not found, please enter Title & Author manually.`,
        { duration: 7000 }
      );
    } finally {
      setIsFetchingMetadata(false);
    }
  };

  // Generate branded author avatar file to satisfy backend multipart requirement
  const generateAuthorAvatar = async (name) => {
    return BookMetadataService.generateAuthorAvatar(name);
  };

  // ─── Quick-Add Missing Author & Complete Autofill ──────────────────────────
  const handleQuickAddAuthor = async (authorName) => {
    setIsAddingAuthor(true);
    try {
      // 1. Check if we already fetched profile, or fetch it now
      let profile = missingAuthorInfo?.authorProfile;
      if (!profile) {
        try {
          profile = await BookMetadataService.fetchAuthorProfile(authorName);
        } catch (err) {
          console.warn("Could not fetch author profile:", err);
        }
      }

      // 2. Prepare author payload with real bio if available
      const bio = profile?.bio
        ? profile.bio.slice(0, 990)
        : `Author of "${missingAuthorInfo?.bookData?.title || "notable works"}".`;

      const newAuthorPayload = {
        authorName: (profile?.authorName || authorName).trim(),
        aboutAuthor: bio,
      };

      // 3. Download real author photo (or fallback to generated branded avatar)
      const imageFile = await BookMetadataService.getAuthorImageFile(
        profile?.imageUrl,
        newAuthorPayload.authorName
      );

      // 4. Send multipart request to backend /authors/create
      const res = await AuthorManager.addAuthor(newAuthorPayload, imageFile);
      const createdAuthor = res.responseData;

      if (createdAuthor && createdAuthor.authorId) {
        const updatedAuthors = [...authors, createdAuthor];
        setAuthors(updatedAuthors);

        if (missingAuthorInfo?.bookData) {
          await applyBookDataToForm(missingAuthorInfo.bookData, createdAuthor.authorId);
          toast.success(
            `Author "${createdAuthor.authorName}" added ${profile?.imageUrl ? "with photo" : ""} & Book "${missingAuthorInfo.bookData.title}" auto-filled!`
          );
        } else {
          toast.success(`Author "${createdAuthor.authorName}" added successfully!`);
        }
        setMissingAuthorInfo(null);
      } else {
        throw new Error(res.responseMessage || "Failed to add author.");
      }
    } catch (err) {
      console.error("Error adding author:", err);
      toast.error(
        err.response?.data?.responseMessage ||
          err.message ||
          "Failed to add author automatically."
      );
    } finally {
      setIsAddingAuthor(false);
    }
  };

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="bg-primary-shade1 text-text-muted p-8 rounded-xl shadow-xl max-w-4xl mx-auto"
      >
        <div className="mb-6">
          <BackButton />
        </div>

        <h2 className="text-3xl font-semibold text-text-black mb-6 text-center">
          📚 {bookId ? "Edit Book" : "Add New Book"}
        </h2>

        {/* ─── AI & Barcode Quick Auto-Fill Bar ────────────────────────────── */}
        <div className="mb-6 p-4 bg-white dark:bg-gray-800 border border-orange-200 dark:border-orange-800/60 rounded-xl shadow-sm">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-orange-600 text-white flex items-center justify-center font-bold text-lg shadow-sm">
                <Sparkles size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white text-sm flex items-center gap-1.5">
                  Quick Book Auto-Fill
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 dark:bg-orange-950/60 dark:text-orange-400">
                    BARCODE SCAN
                  </span>
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Scan barcode with camera or enter ISBN to fill Title, Pages, Publication, Language & Description instantly
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setIsScannerOpen(true)}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold rounded-lg shadow-md hover:shadow-lg transition-all"
              >
                <Camera size={15} />
                <span>Scan Barcode / Enter ISBN</span>
              </button>
            </div>
          </div>
        </div>

        {/* ─── Missing Author Verification & Auto-Photo Banner ───────────────── */}
        {missingAuthorInfo && (
          <div className="mb-6 p-5 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/30 border-2 border-orange-300 dark:border-orange-700/60 rounded-xl shadow-md animate-fadeIn">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              {/* Author Portrait Preview or Loading Spinner */}
              <div className="relative flex-shrink-0">
                {missingAuthorInfo.authorProfile?.thumbnailUrl ? (
                  <img
                    src={missingAuthorInfo.authorProfile.thumbnailUrl}
                    alt={missingAuthorInfo.scannedAuthorName}
                    className="w-16 h-16 rounded-full object-cover border-2 border-orange-500 shadow-md"
                  />
                ) : missingAuthorInfo.isLoadingProfile ? (
                  <div className="w-16 h-16 rounded-full bg-orange-100 dark:bg-orange-900/60 border-2 border-dashed border-orange-400 flex items-center justify-center text-orange-600 animate-pulse">
                    <Sparkles size={22} className="animate-spin" />
                  </div>
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 text-white font-bold text-xl flex items-center justify-center shadow-md">
                    {(missingAuthorInfo.scannedAuthorName || "A")
                      .split(" ")
                      .filter(Boolean)
                      .slice(0, 2)
                      .map((w) => w[0].toUpperCase())
                      .join("")}
                  </div>
                )}
                {missingAuthorInfo.authorProfile?.source && (
                  <span className="absolute -bottom-1 -right-1 px-1.5 py-0.5 text-[9px] font-semibold bg-blue-600 text-white rounded-full shadow">
                    {missingAuthorInfo.authorProfile.source}
                  </span>
                )}
              </div>

              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className="font-bold text-gray-900 dark:text-gray-100 text-sm sm:text-base">
                    Author Not Registered: "{missingAuthorInfo.scannedAuthorName}"
                  </h4>
                  {missingAuthorInfo.authorProfile?.imageUrl && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-950/60 px-2 py-0.5 rounded-full">
                      <CheckCircle2 size={12} /> Photo Found
                    </span>
                  )}
                  {missingAuthorInfo.isLoadingProfile && (
                    <span className="text-[11px] text-orange-600 dark:text-orange-400 animate-pulse font-medium">
                      Searching author photo & details online...
                    </span>
                  )}
                </div>

                <p className="text-xs text-gray-700 dark:text-gray-300 mt-1 leading-relaxed">
                  The scanned book <strong>"{missingAuthorInfo.bookData.title}"</strong> is by{" "}
                  <strong>"{missingAuthorInfo.scannedAuthorName}"</strong>.
                  {missingAuthorInfo.authorProfile?.bio
                    ? ` Bio: "${missingAuthorInfo.authorProfile.bio.slice(0, 140)}..."`
                    : " Add this author now to link with the book and store their profile image."}
                </p>

                <div className="mt-3 flex flex-wrap items-center gap-2.5">
                  <button
                    type="button"
                    disabled={isAddingAuthor}
                    onClick={() => handleQuickAddAuthor(missingAuthorInfo.scannedAuthorName)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-50"
                  >
                    {isAddingAuthor ? (
                      "Adding Author with Photo..."
                    ) : (
                      <>
                        <Sparkles size={14} />
                        Add "{missingAuthorInfo.scannedAuthorName}" {missingAuthorInfo.authorProfile?.imageUrl ? "with Photo" : ""} & Complete Autofill
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (missingAuthorInfo?.bookData) {
                        applyBookDataToForm(missingAuthorInfo.bookData, null);
                      }
                      setMissingAuthorInfo(null);
                      toast.success("Book details auto-filled! Please select author manually.");
                    }}
                    className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 hover:border-orange-500 text-xs font-semibold text-gray-700 dark:text-gray-200 rounded-lg shadow-sm transition-all"
                  >
                    Auto-Fill Book Anyway (Select Author Manually)
                  </button>
                  <button
                    type="button"
                    onClick={() => setMissingAuthorInfo(null)}
                    className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Book Name */}
        <div>
          <label className="block mb-2 font-medium">
            Book Name <span className="text-red-600">*</span>
          </label>
          <AsyncSelect
            cacheOptions
            loadOptions={loadOptions}
            defaultOptions={false}
            placeholder="Search or enter book name"
            isClearable
            inputValue={bookNameInput}
            onInputChange={(newValue, { action }) => {
              if (action === "input-change") {
                setBookNameInput(newValue);
                setValue("bookName", newValue, { shouldValidate: true });
              }
            }}
            onChange={(selectedOption) => {
              if (selectedOption) {
                setBookNameInput(selectedOption.label); // Show selected label
                navigate(`/bookForm/${selectedOption.value}`);
              } else {
                setBookNameInput(""); // Clear on remove
                setValue("bookName", "", { shouldValidate: true });
              }
            }}
            components={{
              DropdownIndicator: null,
              IndicatorSeparator: null,
            }}
            styles={{
              control: (provided, state) => ({
                ...provided,
                minHeight: "40px",
                borderRadius: "0.375rem", // rounded
                paddingLeft: "0.75rem",
                paddingRight: "0.75rem",
                borderColor: errors.bookName ? "#dc2626" : "#ff7236", // red-600 or gray-300
                boxShadow: state.isFocused ? "0 0 0 2px #ff7236" : "none", // primary-shade4
                "&:hover": {
                  borderColor: "#ff7236", // gray-400
                },
              }),
              placeholder: (provided) => ({
                ...provided,
                color: "#6b7280", // text-gray-500
                fontSize: "0.875rem",
              }),
              input: (provided) => ({
                ...provided,
                color: "#374151", // text-gray-700 (to match Publication input text)
                fontSize: "1rem",
              }),
              singleValue: (provided) => ({
                ...provided,
                color: "#374151", // text-gray-700
                fontSize: "1rem",
              }),
            }}
          />

          <input
            type="hidden"
            {...register("bookName", {
              required: "Book Name is required",
              minLength: {
                value: 3,
                message: "At least 3 characters required",
              },
            })}
          />
          {errors.bookName && (
            <span className="text-red-600 text-sm">
              {errors.bookName.message}
            </span>
          )}
        </div>

        {/* Publication */}
        <div>
          <label className="block mb-2 font-medium">
            Publication <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            {...register("bookPublication", {
              required: "Publication is required",
            })}
            placeholder="Enter publication"
            className={`w-full px-4 py-2 rounded border ${
              errors.bookPublication
                ? "border-red-600"
                : "border-primary-shade3"
            } focus:outline-none focus:ring-2 focus:ring-primary-shade4`}
          />
          {errors.bookPublication && (
            <span className="text-red-600 text-sm">
              {errors.bookPublication.message}
            </span>
          )}
        </div>

        {/* ISBN-10 */}
        <div>
          <label className="block mb-2 font-medium">ISBN-10</label>
          <input
            type="text"
            {...register("bookIsbn10", {
              maxLength: { value: 50, message: "ISBN-10 cannot exceed 50 characters" },
            })}
            placeholder="Enter ISBN-10"
            className={`w-full px-4 py-2 rounded border ${
              errors.bookIsbn10 ? "border-red-600" : "border-primary-shade3"
            } focus:outline-none focus:ring-2 focus:ring-primary-shade4`}
          />
          {errors.bookIsbn10 && (
            <span className="text-red-600 text-sm">
              {errors.bookIsbn10.message}
            </span>
          )}
        </div>

        {/* ISBN-13 */}
        <div>
          <label className="block mb-2 font-medium">ISBN-13</label>
          <input
            type="text"
            {...register("bookIsbn13", {
              maxLength: { value: 50, message: "ISBN-13 cannot exceed 50 characters" },
            })}
            placeholder="Enter ISBN-13"
            className={`w-full px-4 py-2 rounded border ${
              errors.bookIsbn13 ? "border-red-600" : "border-primary-shade3"
            } focus:outline-none focus:ring-2 focus:ring-primary-shade4`}
          />
          {errors.bookIsbn13 && (
            <span className="text-red-600 text-sm">
              {errors.bookIsbn13.message}
            </span>
          )}
        </div>

        {/* Pages */}
        <div>
          <label className="block mb-2 font-medium">
            Pages <span className="text-red-600">*</span>
          </label>
          <input
            type="number"
            {...register("pages", {
              required: "Pages is required",
              min: { value: 1, message: "Minimum 1 page" },
            })}
            placeholder="e.g. 200"
            className={`w-full px-4 py-2 rounded border ${
              errors.pages ? "border-red-600" : "border-primary-shade3"
            } focus:outline-none focus:ring-2 focus:ring-primary-shade4`}
          />
          {errors.pages && (
            <span className="text-red-600 text-sm">{errors.pages.message}</span>
          )}
        </div>

        {/* Published Date */}
        <div>
          <label className="block mb-2 font-medium">Published Date</label>
          <input
            type="date"
            {...register("publishedDate")}
            className={`w-full px-4 py-2 rounded border ${
              errors.publishedDate ? "border-red-600" : "border-primary-shade3"
            } focus:outline-none focus:ring-2 focus:ring-primary-shade4`}
          />
          {errors.publishedDate && (
            <span className="text-red-600 text-sm">
              {errors.publishedDate.message}
            </span>
          )}
        </div>

        {/* Dimensions & Weight */}
        {["height", "width", "length", "weight"].map((field) => (
          <div key={field}>
            <label className="block mb-2 font-medium capitalize">
              {field.charAt(0).toUpperCase() + field.slice(1)}
            </label>
            <input
              type="number"
              step="any"
              {...register(field, {
                min: { value: 0.1, message: `Must be positive` },
              })}
              className={`w-full px-4 py-2 rounded border ${
                errors[field] ? "border-red-600" : "border-primary-shade3"
              } focus:outline-none focus:ring-2 focus:ring-primary-shade4`}
            />
            {errors[field] && (
              <span className="text-red-600 text-sm">
                {errors[field].message}
              </span>
            )}
          </div>
        ))}

        {/* Language */}
        <div>
          <label className="block mb-2 font-medium">
            Language <span className="text-red-600">*</span>
          </label>
          <select
            {...register("bookLanguage", { required: "Language is required" })}
            className={`w-full px-4 py-2 rounded border ${
              errors.bookLanguage ? "border-red-600" : "border-primary-shade3"
            } focus:outline-none focus:ring-2 focus:ring-primary-shade4`}
          >
            <option value="">Select Language</option>
            <option value="English">English</option>
            <option value="Hindi">Hindi</option>
            <option value="Marathi">Marathi</option>
          </select>
          {errors.bookLanguage && (
            <span className="text-red-600 text-sm">
              {errors.bookLanguage.message}
            </span>
          )}
        </div>

        {/* Reading Age */}
        <div>
          <label className="block mb-2 font-medium">
            Reading Age <span className="text-red-600">*</span>
          </label>
          <select
            {...register("readingAge", { required: "Reading age is required" })}
            className={`w-full px-4 py-2 rounded border ${
              errors.readingAge ? "border-red-600" : "border-primary-shade3"
            } focus:outline-none focus:ring-2 focus:ring-primary-shade4 bg-white`}
          >
            <option value="">Select Reading Age</option>
            <option value="CHILD">6-9 years</option>
            <option value="PRE_TEEN">10-12 years</option>
            <option value="TEEN">13-17 years</option>
            <option value="ADULT">18+ years</option>
          </select>
          {errors.readingAge && (
            <span className="text-red-600 text-sm">
              {errors.readingAge.message}
            </span>
          )}
        </div>

        {/* Edition */}
        <div>
          <label className="block mb-2 font-medium">Edition</label>
          <input
            type="text"
            {...register("bookEdition")}
            placeholder="e.g. 1st Edition"
            className={`w-full px-4 py-2 rounded border ${
              errors.bookEdition ? "border-red-600" : "border-primary-shade3"
            } focus:outline-none focus:ring-2 focus:ring-primary-shade4`}
          />
          {errors.bookEdition && (
            <span className="text-red-600 text-sm">
              {errors.bookEdition.message}
            </span>
          )}
        </div>

        {/* Copies */}
        <div>
          <label className="block mb-2 font-medium">
            Copies <span className="text-red-600">*</span>
          </label>
          <input
            type="number"
            {...register("bookCopies", {
              required: "Copies is required",
              min: { value: 0, message: "At least 0" },
            })}
            className={`w-full px-4 py-2 rounded border ${
              errors.bookCopies ? "border-red-600" : "border-primary-shade3"
            } focus:outline-none focus:ring-2 focus:ring-primary-shade4`}
          />
          {errors.bookCopies && (
            <span className="text-red-600 text-sm">
              {errors.bookCopies.message}
            </span>
          )}
        </div>

        {/* Genre */}
        <div>
          <label className="block mb-2 font-medium">
            Genre <span className="text-red-600">*</span>
          </label>
          <select
            {...register("bookGenres", { required: "Genre is required" })}
            className={`w-full px-4 py-2 rounded border ${
              errors.bookGenres ? "border-red-600" : "border-primary-shade3"
            } focus:outline-none focus:ring-2 focus:ring-primary-shade4 bg-white`}
          >
            <option value="">Select Genre</option>
            {/* options... */}
            <option value="FICTION">Fiction</option>
            <option value="NON_FICTION">Non-Fiction</option>
            <option value="MYSTERY">Mystery</option>
            <option value="FANTASY">Fantasy</option>
            <option value="SCIENCE_FICTION">Science Fiction</option>
            <option value="BIOGRAPHY">Biography</option>
            <option value="HISTORY">History</option>
            <option value="ROMANCE">Romance</option>
            <option value="HORROR">Horror</option>
            <option value="POETRY">Poetry</option>
            <option value="THRILLER">Thriller</option>
            <option value="ADVENTURE">Adventure</option>
            <option value="SELF_HELP">Self Help</option>
            <option value="GRAPHIC_NOVEL">Graphic Novel</option>
            <option value="PHILOSOPHY">Philosophy</option>
            <option value="RELIGION">Religion</option>
            <option value="EDUCATIONAL">Educational</option>
            <option value="TRAVEL">Travel</option>
            <option value="COOKING">Cooking</option>
            <option value="ART">Art</option>
            <option value="DRAMA">Drama</option>
          </select>
          {errors.bookGenres && (
            <span className="text-red-600 text-sm">
              {errors.bookGenres.message}
            </span>
          )}
        </div>

        {/* Description */}
        <div className="md:col-span-2">
          <label className="block mb-2 font-medium">
            Description <span className="text-red-600">*</span>
          </label>
          <textarea
            {...register("bookDescription", {
              required: "Description is required",
              minLength: { value: 200, message: "At least 200 characters" },
            })}
            rows={4}
            placeholder="Describe the book..."
            className={`w-full px-4 py-2 rounded border ${
              errors.bookDescription
                ? "border-red-600"
                : "border-primary-shade3"
            } focus:outline-none focus:ring-2 focus:ring-primary-shade4 resize-none`}
          />
          {errors.bookDescription && (
            <span className="text-red-600 text-sm">
              {errors.bookDescription.message}
            </span>
          )}
        </div>

        {/* Category */}
        <div className="mb-4">
          <label className="block mb-2 font-medium">
            Category <span className="text-red-600">*</span>
          </label>
          <Controller
            name="categoryId"
            control={control}
            rules={{ required: "Category is required" }}
            render={({ field }) => (
              <Select
                {...field}
                options={categoryOptions}
                styles={customSelectStyles}
                classNamePrefix="react-select"
                placeholder="Select Category"
                value={
                  categoryOptions.find(
                    (option) => option.value === field.value
                  ) || null
                }
                onChange={(selectedOption) =>
                  field.onChange(selectedOption?.value)
                }
              />
            )}
          />
          {errors.categoryId && (
            <span className="text-red-600 text-sm">
              {errors.categoryId.message}
            </span>
          )}
        </div>

        {/* Author */}
        <div className="mb-4">
          <label className="block mb-2 font-medium">
            Author <span className="text-red-600">*</span>
          </label>
          <Controller
            name="authorId"
            control={control}
            rules={{ required: "Author is required" }}
            render={({ field }) => (
              <Select
                {...field}
                options={authorOptions}
                styles={customSelectStyles}
                classNamePrefix="react-select"
                placeholder="Select Author"
                value={
                  authorOptions.find(
                    (option) => option.value === field.value
                  ) || null
                }
                onChange={(selectedOption) =>
                  field.onChange(selectedOption?.value)
                }
              />
            )}
          />
          {errors.authorId && (
            <span className="text-red-600 text-sm">
              {errors.authorId.message}
            </span>
          )}
        </div>

        {/* Status */}
        <div>
          <label className="block mb-2 font-medium">
            Book Status <span className="text-red-600">*</span>
          </label>
          <select
            {...register("bookStatus", { required: "Status is required" })}
            className={`w-full px-4 py-2 rounded border ${
              errors.bookStatus ? "border-red-600" : "border-primary-shade3"
            } focus:outline-none focus:ring-2 focus:ring-primary-shade4 bg-white`}
          >
            <option value="">Select Status</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="ARCHIVED">Archived</option>
            <option value="PENDING_APPROVAL">Pending Approval</option>
          </select>
          {errors.bookStatus && (
            <span className="text-red-600 text-sm">
              {errors.bookStatus.message}
            </span>
          )}
        </div>

        {/* Stock Status */}
        <div>
          <label className="block mb-2 font-medium">
            Book Stock Status
          </label>
          <select
            {...register("bookStockStatus")}
            className={`w-full px-4 py-2 rounded border ${
              errors.bookStockStatus
                ? "border-red-600"
                : "border-primary-shade3"
            } focus:outline-none focus:ring-2 focus:ring-primary-shade4 bg-white`}
          >
            <option value="">Select Stock Status</option>
            <option value="IN_STOCK">In Stock</option>
            <option value="OUT_OF_STOCK">Out of Stock</option>
          </select>
          {errors.bookStockStatus && (
            <span className="text-red-600 text-sm">
              {errors.bookStockStatus.message}
            </span>
          )}
        </div>

        {/* File Upload */}
        <div className="md:col-span-2">
          <label className="block mb-2 font-medium">Upload Book Images</label>
          <div
            className="flex flex-col items-center justify-center w-full p-6 border-2 border-dashed rounded-lg cursor-pointer hover:bg-primary-shade1 transition-colors"
            onClick={() => document.getElementById("fileInput").click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const droppedFiles = Array.from(e.dataTransfer.files);
              setBookImages((prev) => [...prev, ...droppedFiles]); // append if needed
            }}
          >
            <svg
              className="w-10 h-10 mb-3 text-primary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16V4m0 0L3 8m4-4l4 4m6 8v-8m0 0l4 4m-4-4l-4 4"
              />
            </svg>
            <p className="mb-2 text-sm text-gray-500">
              <span className="font-semibold">Click to upload</span> or drag and
              drop
            </p>
            <p className="text-xs text-gray-500">SVG, PNG, JPG (max. 5MB)</p>
            <input
              id="fileInput"
              type="file"
              multiple
              onChange={(e) => {
                // Convert FileList to Array and set it to state
                setBookImages(Array.from(e.target.files));
              }}
              className="hidden"
            />
          </div>

          {/* Preview Section */}
          {bookImages.length > 0 && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {bookImages.map((item, index) => {
                const previewUrl =
                  typeof item === "string"
                    ? item.startsWith("http")
                      ? item
                      : `${config.url}${item}`
                    : URL.createObjectURL(item);
                return (
                  <div
                    key={index}
                    className="relative aspect-[2/3] w-24 sm:w-28 md:w-32 rounded-lg overflow-hidden border border-gray-200 shadow-sm"
                  >
                    <img
                      src={previewUrl}
                      alt={`Preview ${index}`}
                      className="object-contain w-full h-full"
                    />
                    {/* Remove Button */}
                    <button
                      type="button"
                      onClick={() => {
                        setBookImages(bookImages.filter((_, i) => i !== index));
                      }}
                      className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 hover:bg-red-700 transition"
                      title="Remove Image"
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Submit Button */}
      <div className="mt-8 text-center">
        <button
          type="submit"
          disabled={isSubmitting}
          className={`bg-primary text-white px-6 py-3 rounded-lg text-lg transition-all duration-300 ${
            isSubmitting
              ? "opacity-60 cursor-not-allowed"
              : "hover:bg-primary-shade4"
          }`}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <svg
                className="animate-spin h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                />
              </svg>
              Processing...
            </span>
          ) : (
            <>{bookId ? "Save Book" : "Submit Book"}</>
          )}
        </button>
      </div>
    </form>

    {/* Barcode Camera Scanner & Manual ISBN Modal */}
    <BookBarcodeScannerModal
      isOpen={isScannerOpen}
      onClose={() => setIsScannerOpen(false)}
      onBookFound={handleIsbnScan}
      isFetching={isFetchingMetadata}
    />
  </>
);
};

export default BookAddForm;
